// src/App.js
// ============================================================================
// ReserveZoo — application root
// ============================================================================
// Four surfaces, four audiences, each isolated from the others:
//
//   /            public landing
//   /rms/*       the RMS — rental operators
//   /advertise   media kit (public)
//   /advertiser  advertiser self-serve
//   /ad-sales    ad sales console (platform staff)
//   /admin       site admin (platform owner)
//
// Route-level separation is convenience. The real boundary is RLS: see
// rms_is_operator_member / rms_is_advertiser_member / rms_is_ad_admin /
// rms_is_platform_admin in supabase/migrations.
//
// The inherited CompareMechanic marketplace — ~200 components, ~100 services,
// 68 edge functions and its provider stack — has been removed. Nothing in the
// RMS depended on it; src/rms is self-contained.
// ============================================================================

import React, { Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";

import RmsApp from "./rms/RmsApp";
import RmsLanding from "./rms/pages/Landing";
import MediaKit from "./rms/pages/MediaKit";
import AdvertiserPortal from "./rms/pages/AdvertiserPortal";
import AdSales from "./rms/pages/AdSales";
import SiteAdmin from "./rms/pages/SiteAdmin";
import SignIn from "./rms/pages/SignIn";
import JoinInvite from "./rms/pages/JoinInvite";
import SignAgreement from "./rms/pages/SignAgreement";
import SignedAgreement from "./rms/pages/SignedAgreement";
import ResetPassword from "./rms/pages/ResetPassword";
import ErrorBoundary from "./rms/components/ErrorBoundary";
import { AuthProvider, useAuth } from "./rms/context/AuthContext";
import { RequireRole } from "./rms/components/RequireRole";

function Loading() {
  return (
    <div style={{ display: "grid", placeItems: "center", minHeight: "60vh", fontFamily: "system-ui, sans-serif", color: "#475569" }}>
      Loading…
    </div>
  );
}

/**
 * Carries a recovery session to the one page that can finish it.
 *
 * A password recovery link produces a real session wherever it lands, and
 * where it lands is not always /reset: Supabase falls back to the Site URL
 * when the redirect target is missing from its allow-list, which silently
 * signs the person in on the marketing page and strands them. That happened on
 * this project, and the symptom -- "resetting my password just brings me back
 * to the same page" -- gives nobody a clue why.
 *
 * So the redirect is belt and braces rather than the mechanism. Get the
 * allow-list right and this never fires; get it wrong and recovery still
 * works. The flag is set once, from the PASSWORD_RECOVERY event, and cleared
 * as soon as a password is actually chosen.
 */
function RecoveryRedirect() {
  const { recovery } = useAuth();
  const { pathname } = useLocation();
  if (!recovery || pathname === "/reset") return null;
  return <Navigate to="/reset" replace />;
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <Suspense fallback={<Loading />}>
            <RecoveryRedirect />
            <Routes>
              {/* Public */}
              <Route path="/" element={<RmsLanding />} />
              <Route path="/signin" element={<SignIn />} />
              {/* Outside RequireRole on purpose: whoever follows an invitation
                  link may not have an account yet, and the page has to work
                  before they do. */}
              <Route path="/join" element={<JoinInvite />} />
              {/* Outside RequireRole for a different reason than /join: a
                  recovery link DOES sign you in, but sending that session
                  through the operator guards would bounce an advertiser or a
                  platform admin into the vertical picker on the way to a
                  password field. */}
              <Route path="/reset" element={<ResetPassword />} />
              <Route path="/advertise" element={<MediaKit />} />
              {/* The first genuinely CUSTOMER-facing routes in the product.
                  Outside RequireRole because whoever holds the link has
                  probably never heard of ReserveZoo, and outside RmsLayout so
                  no ad surface is ever mounted on a contract -- not suppressed,
                  never rendered. Registered twice each because both pages strip
                  the token out of the path on mount and replace the URL with
                  the bare form. */}
              <Route path="/sign/:token" element={<SignAgreement />} />
              <Route path="/sign" element={<SignAgreement />} />
              <Route path="/signed/:token" element={<SignedAgreement />} />
              <Route path="/signed" element={<SignedAgreement />} />

              {/* Operators — any signed-in user; RLS scopes them to their own
                  operator. RmsApp applies RequireOperator per route. */}
              <Route
                path="/rms/*"
                element={
                  <RequireRole>
                    <RmsApp />
                  </RequireRole>
                }
              />

              {/* Advertisers */}
              <Route path="/advertiser" element={<RequireRole role="advertiser"><AdvertiserPortal /></RequireRole>} />
              <Route path="/advertiser/:tab" element={<RequireRole role="advertiser"><AdvertiserPortal /></RequireRole>} />

              {/* Ad sales staff */}
              <Route path="/ad-sales" element={<RequireRole role="ad_admin"><AdSales /></RequireRole>} />
              <Route path="/ad-sales/:tab" element={<RequireRole role="ad_admin"><AdSales /></RequireRole>} />

              {/* Platform owner */}
              <Route path="/admin" element={<RequireRole role="platform_admin"><SiteAdmin /></RequireRole>} />
              <Route path="/admin/:tab" element={<RequireRole role="platform_admin"><SiteAdmin /></RequireRole>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
